import React from 'react';
import { aboutContent } from '../mock';
import { MapPin, Users, Leaf, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const SupportSection = () => {
  const handleSupport = () => {
    window.location.href = 'https://buy.stripe.com/aFa5kF7998BJa1CaqFb3q02';
  };

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Subtle Background Image */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1516684669134-de6f7c473a2a?w=1920&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Content */}
      <div className="container mx-auto px-4 max-w-3xl text-center relative z-10">
        {/* Small Maple Leaf Accent */}
        <div className="flex justify-center mb-8">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#FF8C1915' }}
          >
            <Heart className="h-8 w-8" style={{ color: '#FF8C19' }} />
          </div>
        </div>

        {/* Title */}
        <h2 
          className="text-4xl md:text-5xl font-bold mb-4" 
          style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}
        >
          Support the Journey
        </h2>

        {/* Subtitle */}
        <p 
          className="text-lg md:text-xl mb-8 italic"
          style={{ color: '#736c64' }}
        >
          A small batch operation, built with care, intention, and respect for the process.
        </p>

        {/* Body Text */}
        <div className="space-y-6 mb-10" style={{ color: '#736c64' }}>
          <p className="text-lg leading-relaxed">
            We're a two-person maple operation, growing season by season, learning, refining, 
            and putting everything we have into what we create.
          </p>
          <p className="text-lg leading-relaxed">
            What started as a small project has continued to expand, and with it, our vision. 
            Each step forward allows us to invest more into our process, our equipment, and 
            the quality of what we produce.
          </p>
          <p className="text-lg leading-relaxed">
            If you feel connected to what we're building and want to support us beyond 
            purchasing syrup, you can contribute here.
          </p>
          <p className="text-lg font-medium" style={{ color: '#4a2c1a' }}>
            It's never expected, always appreciated.
          </p>
        </div>

        {/* Support Button */}
        <Button
          onClick={handleSupport}
          className="text-white font-semibold px-10 py-6 text-lg hover:opacity-90 transition-all shadow-lg"
          style={{ backgroundColor: '#FF8C19' }}
        >
          <Heart className="h-5 w-5 mr-2" />
          Support the Journey
        </Button>

        {/* Disclaimer */}
        <p 
          className="text-sm mt-6"
          style={{ color: '#9ca3af' }}
        >
          This is a voluntary contribution and is not a charitable or tax-deductible donation.
        </p>
      </div>
    </section>
  );
};

const About = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f7f5f2' }}>
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: '#4a2c1a' }}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#FF8C19', fontFamily: 'Georgia, serif' }}>
            {aboutContent.title}
          </h1>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: '#f7f5f2' }}>
            From our family's trees to your breakfast table
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Story */}
          <div className="bg-white rounded-lg p-8 md:p-12 shadow-lg mb-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#FF8C19' }}>
                <Leaf className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#4a2c1a' }}>
                Our Beginning
              </h2>
            </div>
            <p className="text-lg leading-relaxed" style={{ color: '#736c64' }}>
              {aboutContent.story}
            </p>
          </div>

          {/* Team */}
          <div className="bg-white rounded-lg p-8 md:p-12 shadow-lg mb-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#FF8C19' }}>
                <Users className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#4a2c1a' }}>
                Who We Are
              </h2>
            </div>
            
            {/* Founders Photo */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative mb-4">
                <img 
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_bc765b07-9ab8-4ca1-a515-ac0578ecccfb/artifacts/dwg5pg5m_IMG_0408-2.webp"
                  alt="Marco and Adam - Founders"
                  className="w-80 h-80 object-cover rounded-lg shadow-xl border-4"
                  style={{ borderColor: '#FF8C19' }}
                />
              </div>
              <p className="text-lg font-semibold" style={{ color: '#4a2c1a' }}>
                Marco & Adam - Founders of The Maple Daddies
              </p>
            </div>

            <p className="text-lg leading-relaxed" style={{ color: '#736c64' }}>
              {aboutContent.team}
            </p>
          </div>

          {/* Location */}
          <div className="bg-white rounded-lg p-8 md:p-12 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#FF8C19' }}>
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#4a2c1a' }}>
                Where We Are
              </h2>
            </div>
            <p className="text-lg leading-relaxed" style={{ color: '#736c64' }}>
              {aboutContent.location}
            </p>
          </div>
        </div>
      </section>

      {/* Support the Journey Section */}
      <SupportSection />

      {/* Process Section */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}>
            Our Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF8C19' }}>
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#4a2c1a' }}>Tapping</h3>
              <p className="text-sm" style={{ color: '#736c64' }}>We carefully tap our maple trees during peak season</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF8C19' }}>
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#4a2c1a' }}>Collection</h3>
              <p className="text-sm" style={{ color: '#736c64' }}>Fresh sap is collected and transported to our sugarhouse</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF8C19' }}>
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#4a2c1a' }}>Boiling</h3>
              <p className="text-sm" style={{ color: '#736c64' }}>Traditional boiling process concentrates the sap</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF8C19' }}>
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#4a2c1a' }}>Bottling</h3>
              <p className="text-sm" style={{ color: '#736c64' }}>Hand-bottled and sealed for freshness</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
